import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Panel, StatusPill, EmptyState, money, dt } from "../components/lux";
import { ArrowLeft, Trash, ArrowCounterClockwise, Eye } from "@phosphor-icons/react";

export default function DeletedBookings() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [converted, setConverted] = useState(0);
  const [showConverted, setShowConverted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);
  const [restoring, setRestoring] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/deleted-bookings", { params: { include_converted: showConverted } });
      setRows(data.rows || []);
      setConverted(data.converted_count || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [showConverted]);

  useEffect(() => { load(); }, [load]);

  const restore = async (snap) => {
    if (!window.confirm(`Restore this booking for ${snap.customer_name || "Guest"}? It returns with its original id and every figure returns to what it was.`)) return;
    setRestoring(snap.id);
    try {
      await api.post(`/admin/deleted-bookings/${snap.id}/restore`);
      toast.success("Booking restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestoring(""); }
  };

  const openView = async (snap) => {
    try {
      const { data } = await api.get(`/admin/deleted-bookings/${snap.id}`);
      setView(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (user && user.role !== "admin") return <Navigate to="/settings" replace />;

  return (
    <div className="space-y-6" data-testid="deleted-bookings-page">
      <div>
        <Link to="/settings" className="label-mono inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-settings">
          <ArrowLeft size={14} /> Settings
        </Link>
        <h1 className="font-heading text-3xl mt-2 flex items-center gap-2"><Trash size={24} className="text-primary" /> Deleted bookings</h1>
        <p className="text-sm text-muted-foreground mt-1">The recycle bin. Every deleted booking is kept here and can be restored with its original id. Nothing is ever purged automatically.</p>
      </div>

      <Panel label="Recycle bin" title={`${rows.length} booking${rows.length === 1 ? "" : "s"}`} testid="deleted-panel"
        actions={
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="show-converted-toggle">
            <input type="checkbox" checked={showConverted} onChange={(e) => setShowConverted(e.target.checked)} />
            Show converted to lead{converted ? ` (${converted})` : ""}
          </label>
        }>
        {loading ? <div className="p-10 text-center text-muted-foreground">Loading…</div>
          : rows.length === 0 ? <EmptyState icon={Trash} title="Nothing deleted" hint="Deleted bookings will appear here." testid="deleted-empty" />
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="deleted-table">
                  <thead>
                    <tr className="border-b border-border text-left">
                      {["Deleted", "By", "Reason", "Type", "Client", "Service date", "Vehicle", "Salesperson", "Total", "Actions"].map((h) => (
                        <th key={h} className="px-4 py-3 label-mono font-normal whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-border/40 hover:bg-white/[0.03]" data-testid={`deleted-row-${r.id}`}>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">{dt(r.deleted_at, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                        <td className="px-4 py-3 text-xs">{r.deleted_by_name || "—"}</td>
                        <td className="px-4 py-3 text-xs max-w-[160px] truncate" title={r.reason}>{r.source === "convert_to_lead" ? "Converted to lead" : (r.reason || "—")}</td>
                        <td className="px-4 py-3 capitalize">{r.booking_type || "—"}</td>
                        <td className="px-4 py-3 font-heading">{r.customer_name || "Guest"}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">{r.service_date ? dt(r.service_date, { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{r.vehicle_label || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{r.salesperson || "—"}</td>
                        <td className="px-4 py-3 money whitespace-nowrap">{money(r.total)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Button size="sm" variant="outline" onClick={() => openView(r)} data-testid={`deleted-view-${r.id}`}><Eye size={12} className="mr-1" />View</Button>
                          <Button size="sm" className="ml-1.5 bg-primary text-primary-foreground hover:bg-primary/90" disabled={restoring === r.id} onClick={() => restore(r)} data-testid={`deleted-restore-${r.id}`}>
                            <ArrowCounterClockwise size={12} className="mr-1" />{restoring === r.id ? "Restoring…" : "Restore"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="deleted-view-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{view?.customer_name || "Guest"} · deleted booking</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3 mt-2 text-sm">
              <div className="flex flex-wrap gap-3 items-center">
                <StatusPill status={view.status} />
                <span className="capitalize text-muted-foreground">{view.booking_type}</span>
                <span className="money">{money(view.total)}</span>
              </div>
              <div className="border border-border rounded-xl divide-y divide-border/40">
                {Object.entries(view.booking || {}).map(([k, v]) => (
                  <div key={k} className="flex gap-3 px-3 py-1.5">
                    <div className="label-mono w-48 shrink-0">{k}</div>
                    <div className="break-all text-muted-foreground">{v === null || v === "" ? "—" : (typeof v === "object" ? JSON.stringify(v) : String(v))}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
